import { useSearchParams } from 'react-router';
import { styled } from 'styled-components';

import { Button } from '#/components/Button/Button';
import { Body } from '#/components/Text/Body';
import { H2 } from '#/components/Text/H2';
import { H4 } from '#/components/Text/H4';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useSearchActions } from '#/modules/search/contexts/SearchContext';
import { useSearchRefinements } from '#/modules/search/hooks/refinements/useSearchRefinements';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { colors, gradients } from '#/theme/theme';
import { OBJECT_TYPE_PLURAL_HUMAN_NAME_MAP } from '#/utils/constants/general/objectTypePluralHumanNameMap';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { getSearchResultsEmptyMessage } from './getSearchResultsEmptyMessage';
import {
  SearchResultsHitsEmpty_Box_1,
  SearchResultsHitsEmpty_Box_2,
  SearchResultsHitsEmpty_Box_3,
  SearchResultsHitsEmpty_Hide_1,
} from './SearchResultsHitsEmpty.styled';

const Tile = styled.div`
  ${gradients.skyBlueToBrandBlueTile};
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.1);
  background: ${colors.white};
  position: relative;
  display: flex;
  flex: 1;
  padding-block: 64px;
  padding-inline: 32px;
  border-radius: 4px;
`;

type Props = {
  searchType: SearchType;
  query: string | undefined;
  searchLocation: SearchLocation | undefined;
};

export function SearchResultsHitsEmpty({
  searchType,
  query,
  searchLocation,
}: Props) {
  const { i18n } = useI18n();
  const { clearSearchLocation } = useSearchActions();

  const [searchParams, setSearchParams] = useSearchParams();
  const resultType = OBJECT_TYPE_PLURAL_HUMAN_NAME_MAP(i18n)[searchType];
  const { clearAllRefinements, hasActiveRefinements } =
    useSearchRefinements(searchType);

  const clearQuery = () => {
    searchParams.delete('q');
    clearSearchLocation(false);
    setSearchParams(searchParams);
  };

  const message = getSearchResultsEmptyMessage(
    i18n,
    resultType,
    query,
    searchLocation,
  );

  return (
    <div data-qa-id="search-results-hits-empty" style={{ marginTop: 32 }}>
      <SearchResultsHitsEmpty_Box_3>
        <div style={{ flex: '1 1 auto', paddingRight: 16 }}>
          <H4 style={{ marginBottom: 0 }}>
            {i18n('No %(resultType)s match your search', {
              resultType,
            })}
            {message && ': '}
            {message}
          </H4>
        </div>

        <div style={{ lineHeight: 1.75, flex: '0 0 auto' }}>
          {hasActiveRefinements ? (
            <Button
              qaId="search-results-hits-empty-clear-refinements"
              onClick={clearAllRefinements}
              type="button"
              variant="tertiary"
            >
              {i18n('Clear all filters')}
            </Button>
          ) : (
            <Button
              qaId="search-results-hits-empty-clear-query"
              onClick={clearQuery}
              type="button"
              variant="tertiary"
            >
              {i18n('Clear search')}
            </Button>
          )}
        </div>
      </SearchResultsHitsEmpty_Box_3>

      <Tile>
        <SearchResultsHitsEmpty_Box_2>
          <SearchResultsHitsEmpty_Hide_1>
            <img
              src={getStaticImageSrc('broken-computer.webp')}
              alt="Illustration"
              width={256}
              height={201}
            />
          </SearchResultsHitsEmpty_Hide_1>
        </SearchResultsHitsEmpty_Box_2>
        <SearchResultsHitsEmpty_Box_1>
          <H2 style={{ marginBottom: 2 }}>
            {i18n('Looking for additional help?')}
          </H2>
          <Body style={{ marginBottom: 4 }}>
            {i18n('Visit our Help Center for more info.')}
          </Body>
          <Button
            type="link"
            variant="primary"
            href={
              /* TRANSLATORS: search: help center link url */
              i18n('/en/help')
            }
          >
            {
              /* TRANSLATORS: search: help center link text */
              i18n('Help Center')
            }
          </Button>
        </SearchResultsHitsEmpty_Box_1>
      </Tile>
    </div>
  );
}
